import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpTrayIcon,
  ItalicIcon,
  PhotoIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";

const CANVAS_WIDTH = 1080;
const CANVAS_HEIGHT = 1920;

const InstagramStoryEditor = () => {
  const navigate = useNavigate();

  // Canvas and image state
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });
  const [imageScale, setImageScale] = useState(1);
  const [backgroundColor, setBackgroundColor] = useState("#ffffff");
  const [useBackgroundColor, setUseBackgroundColor] = useState(false);
  const [touchId, setTouchId] = useState<number | null>(null);
  const [draggingStatIndex, setDraggingStatIndex] = useState<number | null>(
    null
  );
  const [selectedStatIndex, setSelectedStatIndex] = useState<number | null>(
    null
  );

  // Stat overlay state
  const [statOverlays, setStatOverlays] = useState<
    Array<{
      image: HTMLImageElement;
      x: number;
      y: number;
      width: number;
      height: number;
      originalWidth: number;
      originalHeight: number;
      scale: number;
      isDragging: boolean;
      dragOffset: { x: number; y: number };
    }>
  >([]);
  const [availableStats, setAvailableStats] = useState<string[]>([]);

  // Text state
  const [text, setText] = useState("");
  const [textPos, setTextPos] = useState({ x: 200, y: 200 });
  const [isDraggingText, setIsDraggingText] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [textColor, setTextColor] = useState("white");
  const [fontFamily, setFontFamily] = useState("sans-serif");
  const [textSize, setTextSize] = useState(48);

  // Control switcher state
  const [activeIndex, setActiveIndex] = useState(0);

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Draw background
    if (useBackgroundColor) {
      // Fill with background color
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    } else if (image) {
      // Draw image background
      const baseScale = Math.max(
        CANVAS_WIDTH / image.width,
        CANVAS_HEIGHT / image.height
      );
      const drawWidth = image.width * baseScale * imageScale;
      const drawHeight = image.height * baseScale * imageScale;

      ctx.drawImage(image, imageOffset.x, imageOffset.y, drawWidth, drawHeight);
    }

    // Draw stat overlays
    statOverlays.forEach((overlay) => {
      const scaledWidth = overlay.originalWidth * overlay.scale;
      const scaledHeight = overlay.originalHeight * overlay.scale;
      ctx.drawImage(
        overlay.image,
        overlay.x,
        overlay.y,
        scaledWidth,
        scaledHeight
      );
    });

    // Set font properties ONCE before drawing text
    ctx.font = `${textSize}px ${fontFamily}`;
    ctx.fillStyle = textColor;
    ctx.strokeStyle = "black";
    ctx.lineWidth = 4;

    // Draw both stroke and fill text
    ctx.strokeText(text, textPos.x, textPos.y);
    ctx.fillText(text, textPos.x, textPos.y);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const touch = e.touches[0];
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const x = (touch.clientX - rect.left) * scaleX;
    const y = (touch.clientY - rect.top) * scaleY;

    // Check if touched on a stat overlay (check from top to bottom)
    for (let i = statOverlays.length - 1; i >= 0; i--) {
      const overlay = statOverlays[i];
      const scaledWidth = overlay.originalWidth * overlay.scale;
      const scaledHeight = overlay.originalHeight * overlay.scale;
      if (
        x >= overlay.x &&
        x <= overlay.x + scaledWidth &&
        y >= overlay.y &&
        y <= overlay.y + scaledHeight
      ) {
        setDraggingStatIndex(i);
        setSelectedStatIndex(i);
        setActiveIndex(2); // Switch to stat controls panel (index 2)
        setTouchId(touch.identifier);
        setStatOverlays((prev) =>
          prev.map((overlay, index) => {
            if (index === i) {
              return {
                ...overlay,
                isDragging: true,
                dragOffset: {
                  x: x - overlay.x,
                  y: y - overlay.y,
                },
              };
            }
            return overlay;
          })
        );
        e.preventDefault();
        return;
      }
    }

    // Check if touched on text
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.font = `${textSize}px ${fontFamily}`;
    const textWidth = ctx.measureText(text).width;
    const textHeight = textSize;

    if (
      x >= textPos.x &&
      x <= textPos.x + textWidth &&
      y >= textPos.y - textHeight &&
      y <= textPos.y
    ) {
      setIsDraggingText(true);
      setSelectedStatIndex(null); // Deselect stat when touching text
      setActiveIndex(1); // Switch to text controls panel (index 1)
      setTouchId(touch.identifier);
      setDragOffset({
        x: x - textPos.x,
        y: y - textPos.y,
      });
      e.preventDefault();
    } else {
      // Clicked on empty canvas, deselect stat
      setSelectedStatIndex(null);
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (
      (!isDraggingText && draggingStatIndex === null) ||
      !canvasRef.current ||
      touchId === null
    )
      return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    // Find our specific touch
    const touch = Array.from(e.touches).find((t) => t.identifier === touchId);
    if (!touch) return;

    const touchX = (touch.clientX - rect.left) * scaleX;
    const touchY = (touch.clientY - rect.top) * scaleY;

    if (isDraggingText) {
      const x = touchX - dragOffset.x;
      const y = touchY - dragOffset.y;

      setTextPos({
        x: Math.max(0, Math.min(CANVAS_WIDTH, x)),
        y: Math.max(0, Math.min(CANVAS_HEIGHT, y)),
      });
    } else if (draggingStatIndex !== null) {
      setStatOverlays((prev) =>
        prev.map((overlay, index) => {
          if (index === draggingStatIndex && overlay.isDragging) {
            const scaledWidth = overlay.originalWidth * overlay.scale;
            const scaledHeight = overlay.originalHeight * overlay.scale;
            const x = Math.max(
              0,
              Math.min(
                CANVAS_WIDTH - scaledWidth,
                touchX - overlay.dragOffset.x
              )
            );
            const y = Math.max(
              0,
              Math.min(
                CANVAS_HEIGHT - scaledHeight,
                touchY - overlay.dragOffset.y
              )
            );
            return { ...overlay, x, y };
          }
          return overlay;
        })
      );
    }
  };

  const handleTouchEnd = () => {
    setIsDraggingText(false);
    setDraggingStatIndex(null);
    setTouchId(null);

    // Reset dragging state for all stat overlays
    setStatOverlays((prev) =>
      prev.map((overlay) => ({
        ...overlay,
        isDragging: false,
      }))
    );
  };

  useEffect(() => {
    drawCanvas();
  }, [
    image,
    imageOffset,
    imageScale,
    backgroundColor,
    useBackgroundColor,
    text,
    textPos,
    textColor,
    fontFamily,
    textSize,
    statOverlays,
  ]);

  useEffect(() => {
    // This assumes you have a way to get the list of stat images
    // In a real app, you might import them or fetch from an API
    const stats = [
      "stat1.png",
      "stat2.png",
      "stat3.png",
      // Add all your stat image filenames here
    ];
    setAvailableStats(stats);
  }, []);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const img = new Image();
      img.onload = () => {
        setImage(img);
        // Reset image scale and center the image
        setImageScale(1);
        const baseScale = Math.max(
          CANVAS_WIDTH / img.width,
          CANVAS_HEIGHT / img.height
        );
        const width = img.width * baseScale;
        const height = img.height * baseScale;
        setImageOffset({
          x: (CANVAS_WIDTH - width) / 2,
          y: (CANVAS_HEIGHT - height) / 2,
        });
      };
      img.src = URL.createObjectURL(file);
    }
  };

  const addStatOverlay = (statName: string) => {
    const img = new Image();
    img.onload = () => {
      setStatOverlays((prev) => [
        ...prev,
        {
          image: img,
          x: CANVAS_WIDTH / 2 - img.width / 2,
          y: CANVAS_HEIGHT / 2 - img.height / 2,
          width: img.width,
          height: img.height,
          originalWidth: img.width,
          originalHeight: img.height,
          scale: 1,
          isDragging: false,
          dragOffset: { x: 0, y: 0 },
        },
      ]);
    };
    img.onerror = () => {
      console.error(`Failed to load stat overlay: ${statName}`);
    };
    // Path to public folder stat overlays
    img.src = `/statOverlays/${statName}`;
  };

  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    // Check if clicked on a stat overlay (check from top to bottom)
    for (let i = statOverlays.length - 1; i >= 0; i--) {
      const overlay = statOverlays[i];
      const scaledWidth = overlay.originalWidth * overlay.scale;
      const scaledHeight = overlay.originalHeight * overlay.scale;
      if (
        x >= overlay.x &&
        x <= overlay.x + scaledWidth &&
        y >= overlay.y &&
        y <= overlay.y + scaledHeight
      ) {
        setDraggingStatIndex(i);
        setSelectedStatIndex(i);
        setActiveIndex(2); // Switch to stat controls panel (index 2)
        setStatOverlays((prev) =>
          prev.map((overlay, index) => {
            if (index === i) {
              return {
                ...overlay,
                isDragging: true,
                dragOffset: {
                  x: x - overlay.x,
                  y: y - overlay.y,
                },
              };
            }
            return overlay;
          })
        );
        e.preventDefault();
        return;
      }
    }

    // Check if clicked on text
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.font = `${textSize}px ${fontFamily}`;
    const textWidth = ctx.measureText(text).width;
    const textHeight = textSize;

    if (
      x >= textPos.x &&
      x <= textPos.x + textWidth &&
      y >= textPos.y - textHeight &&
      y <= textPos.y
    ) {
      setIsDraggingText(true);
      setSelectedStatIndex(null); // Deselect stat when clicking text
      setActiveIndex(1); // Switch to text controls panel (index 1)
      setDragOffset({
        x: x - textPos.x,
        y: y - textPos.y,
      });
      e.preventDefault();
    } else {
      // Clicked on empty canvas, deselect stat
      setSelectedStatIndex(null);
    }
  };

  useEffect(() => {
    if (isDraggingText || draggingStatIndex !== null) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDraggingText, draggingStatIndex, dragOffset]);

  useEffect(() => {
    if (isDraggingText || draggingStatIndex !== null) {
      window.addEventListener("touchmove", handleTouchMove, { passive: false });
      window.addEventListener("touchend", handleTouchEnd);
    }

    return () => {
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDraggingText, draggingStatIndex, dragOffset, touchId]);

  const handleMouseMove = (e: MouseEvent) => {
    if ((!isDraggingText && draggingStatIndex === null) || !canvasRef.current)
      return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const mouseX = (e.clientX - rect.left) * scaleX;
    const mouseY = (e.clientY - rect.top) * scaleY;

    if (isDraggingText) {
      const x = mouseX - dragOffset.x;
      const y = mouseY - dragOffset.y;

      setTextPos({
        x: Math.max(0, Math.min(CANVAS_WIDTH, x)),
        y: Math.max(0, Math.min(CANVAS_HEIGHT, y)),
      });
    } else if (draggingStatIndex !== null) {
      setStatOverlays((prev) =>
        prev.map((overlay, index) => {
          if (index === draggingStatIndex && overlay.isDragging) {
            const scaledWidth = overlay.originalWidth * overlay.scale;
            const scaledHeight = overlay.originalHeight * overlay.scale;
            const x = Math.max(
              0,
              Math.min(
                CANVAS_WIDTH - scaledWidth,
                mouseX - overlay.dragOffset.x
              )
            );
            const y = Math.max(
              0,
              Math.min(
                CANVAS_HEIGHT - scaledHeight,
                mouseY - overlay.dragOffset.y
              )
            );
            return { ...overlay, x, y };
          }
          return overlay;
        })
      );
    }
  };

  const handleMouseUp = () => {
    setIsDraggingText(false);
    setDraggingStatIndex(null);

    // Reset dragging state for all stat overlays
    setStatOverlays((prev) =>
      prev.map((overlay) => ({
        ...overlay,
        isDragging: false,
      }))
    );
  };

  const handleDownload = () => {
    if (!canvasRef.current) return;
    const link = document.createElement("a");
    link.download = "instagram-story.png";
    link.href = canvasRef.current.toDataURL("image/png");
    link.click();
  };

  const handleRefresh = () => {
    // Clear all overlays and text
    setStatOverlays([]);
    setText("");
    setTextPos({ x: 200, y: 200 });

    // Reset background settings
    setUseBackgroundColor(false);
    setBackgroundColor("#ffffff");

    // Reset any dragging states
    setIsDraggingText(false);
    setDraggingStatIndex(null);
    setSelectedStatIndex(null);
    setTouchId(null);
  };

  const handleStatScaleChange = (scale: number) => {
    if (selectedStatIndex === null) return;

    setStatOverlays((prev) =>
      prev.map((overlay, index) => {
        if (index === selectedStatIndex) {
          return { ...overlay, scale };
        }
        return overlay;
      })
    );
  };

  const handleDeleteSelectedStat = () => {
    if (selectedStatIndex === null) return;

    setStatOverlays((prev) =>
      prev.filter((_, index) => index !== selectedStatIndex)
    );
    setSelectedStatIndex(null);
  };

  const handleExit = () => {
    // Clear the canvas
    handleRefresh();
    // Navigate to diary
    navigate("/diary");
  };

  const handleResetImagePosition = () => {
    if (!image) return;

    const baseScale = Math.max(
      CANVAS_WIDTH / image.width,
      CANVAS_HEIGHT / image.height
    );
    const width = image.width * baseScale * imageScale;
    const height = image.height * baseScale * imageScale;
    setImageOffset({
      x: (CANVAS_WIDTH - width) / 2,
      y: (CANVAS_HEIGHT - height) / 2,
    });
  };

  const controls = [
    <div className="p-2">
      <p className="text-xs mb-3 font-semibold">Image Settings</p>
      {/* Background Type Toggle */}
      <div className="space-y-2 mb-4">
        <label className="text-xs ml-1">Background Type</label>
        <div className="flex space-x-2">
          <button
            onClick={() => setUseBackgroundColor(false)}
            className={`px-3 py-1 text-xs rounded ${
              !useBackgroundColor
                ? "bg-primary text-white"
                : "bg-base-200 text-base-content/80"
            }`}
          >
            Image
          </button>
          <button
            onClick={() => setUseBackgroundColor(true)}
            className={`px-3 py-1 text-xs rounded ${
              useBackgroundColor
                ? "bg-primary text-white"
                : "bg-base-200 text-base-content/80"
            }`}
          >
            Color
          </button>
        </div>
      </div>

      {/* Background Color Picker */}
      {useBackgroundColor && (
        <div className="space-y-1 items-center mt-1">
          <label className="text-xs ml-1">Background Color</label>
          <input
            type="color"
            className="w-full h-10 rounded border border-base-300"
            value={backgroundColor}
            onChange={(e) => setBackgroundColor(e.target.value)}
          />
        </div>
      )}

      {/* Image Upload */}
      {!useBackgroundColor && (
        <div className="space-y-1 items-center mt-1">
          <label className="text-xs ml-1">Upload Image</label>
          <input
            type="file"
            className="file-input w-full bg-base-100"
            accept="image/*"
            onChange={handleImageUpload}
          />
        </div>
      )}
      {/* Image Controls - Only show when using image background */}
      {!useBackgroundColor && image && (
        <>
          <div className="space-y-1 items-center mt-1">
            <label className="text-xs ml-1">Image Zoom</label>
            <input
              type="range"
              min="0.5"
              max="2"
              step="0.01"
              className="range"
              value={imageScale}
              onChange={(e) => setImageScale(parseFloat(e.target.value))}
            />
          </div>
          <div className="space-y-1 items-center mt-1">
            <label className="text-xs ml-1">Vertical Position</label>
            <input
              type="range"
              min={-CANVAS_HEIGHT}
              max={CANVAS_HEIGHT}
              step="10"
              className="range"
              value={imageOffset.y}
              onChange={(e) =>
                setImageOffset((prev) => ({
                  ...prev,
                  y: parseFloat(e.target.value),
                }))
              }
            />
            <div className="text-xs text-base-content/60 text-center">
              {imageOffset.y}px
            </div>
          </div>
          <div className="space-y-1 items-center mt-1">
            <label className="text-xs ml-1">Horizontal Position</label>
            <input
              type="range"
              min={-CANVAS_WIDTH}
              max={CANVAS_WIDTH}
              step="10"
              className="range"
              value={imageOffset.x}
              onChange={(e) =>
                setImageOffset((prev) => ({
                  ...prev,
                  x: parseFloat(e.target.value),
                }))
              }
            />
            <div className="text-xs text-base-content/60 text-center">
              {imageOffset.x}px
            </div>
          </div>
          <div className="mt-2">
            <button
              onClick={handleResetImagePosition}
              className="btn btn-outline btn-sm w-full"
            >
              Reset Position
            </button>
          </div>
        </>
      )}
    </div>,
    <div className="p-2 w-full">
      <div>
        <p className="text-xs mb-3 font-semibold">Overlay Text Settings</p>
        <div className="space-y-1 items-center mt-1">
          <label className="text-xs ml-1">Overlay text</label>
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="border px-2 py-1 input w-full"
            placeholder="Enter text overlay here"
          />
        </div>
        <div className="space-y-1 items-center mt-1">
          <label className="text-xs ml-1">Text Colour</label>
          <select
            className="border px-2 py-1 select w-full"
            value={textColor}
            onChange={(e) => setTextColor(e.target.value)}
          >
            <option value="white">White</option>
            <option value="black">Black</option>
            <option value="red">Red</option>
            <option value="blue">Blue</option>
            <option value="yellow">Yellow</option>
          </select>
        </div>

        <div className="space-y-1 items-center mt-1">
          <label className="text-xs ml-1">Font Style</label>
          <select
            className="border px-2 py-1 select w-full"
            value={fontFamily}
            onChange={(e) => setFontFamily(e.target.value)}
          >
            <option value="sans-serif">Sans</option>
            <option value="serif">Serif</option>
            <option value="monospace">Mono</option>
            <option value="cursive">Cursive</option>
            <option value="fantasy">Fantasy</option>
          </select>
        </div>

        <div className="space-y-1 items-center mt-1">
          <label className="text-xs ml-1">
            Text Size <span className="font-bold">px</span>
          </label>
          <input
            type="range"
            min="12"
            max="500"
            step="1"
            className="w-full range"
            value={textSize}
            onChange={(e) => setTextSize(parseInt(e.target.value))}
          />
        </div>
      </div>
    </div>,
    <div className="p-2">
      <p className="text-xs mb-3 font-semibold">Stat Overlay Controls</p>

      {/* Stat Size Control */}
      {selectedStatIndex !== null && (
        <div className="space-y-2 mb-4">
          <div className="space-y-1">
            <label className="text-xs ml-1">
              Stat Size <span className="font-bold">%</span>
            </label>
            <input
              type="range"
              min="0.1"
              max="3"
              step="0.1"
              className="w-full range"
              value={statOverlays[selectedStatIndex]?.scale || 1}
              onChange={(e) =>
                handleStatScaleChange(parseFloat(e.target.value))
              }
            />
            <div className="text-xs text-base-content/60 text-center">
              {Math.round((statOverlays[selectedStatIndex]?.scale || 1) * 100)}%
            </div>
          </div>
          <button
            onClick={handleDeleteSelectedStat}
            className="btn btn-error btn-outline btn-sm w-full"
          >
            Delete Selected Stat
          </button>
        </div>
      )}

      <p className="text-xs mb-3 font-semibold">Add New Stat</p>
      <div className="">
        {availableStats.map((stat, index) => (
          <button
            key={index}
            onClick={() => addStatOverlay(stat)}
            className="p-2 border rounded hover:bg-base-200"
          >
            <img
              src={`/statOverlays/${stat}`}
              alt={stat}
              className="w-full h-auto"
              onError={(e) => {
                console.error(`Failed to load preview image: ${stat}`);
                e.currentTarget.style.display = "none";
              }}
            />
            <span className="text-xs">{stat.replace(".png", "")}</span>
          </button>
        ))}
      </div>

      {selectedStatIndex === null && (
        <p className="text-xs text-base-content/60 mt-2">
          Click on a stat overlay to resize it
        </p>
      )}
    </div>,
    <div className="p-2">
      <div className="space-y-2">
        <button onClick={handleRefresh} className="btn btn-outline w-full">
          Clear Canvas
        </button>
        <button onClick={handleDownload} className="btn btn-primary w-full">
          Download Story
        </button>
      </div>
    </div>,
  ];

  const icons = [
    <PhotoIcon className="w-6 h-6" />,
    <ItalicIcon className="w-6 h-6" />,
    <PlusIcon className="w-6 h-6" />,
    <ArrowUpTrayIcon className="w-7 h-7" />,
  ];

  return (
    <div>
      {/* Exit Button */}
      <button
        onClick={handleExit}
        className="absolute top-10 left-4 z-50 flex items-center justify-center w-10 h-10 bg-base-100 border border-base-300 rounded-full shadow-lg hover:bg-base-100 transition-colors"
      >
        <XMarkIcon className="w-5 h-5 text-base-content/80" />
      </button>

      <div className="flex flex-col md:flex-row">
        {/* CANVAS Column */}
        <div className="w-full md:w-1/2 flex flex-col items-center justify-center">
          <div className="flex h-[55vh] justify-center w-full max-w-[400px]">
            <canvas
              ref={canvasRef}
              width={CANVAS_WIDTH}
              height={CANVAS_HEIGHT}
              onMouseDown={handleCanvasMouseDown}
              onTouchStart={handleTouchStart}
              style={{
                border: "1px solid #ccc",
                height: "auto",
                aspectRatio: "9/16",
                cursor: isDraggingText ? "grabbing" : "default",
                touchAction: "none", // Prevent default touch behaviors like scrolling
              }}
            />
          </div>

          <div className="md:hidden">
            <div className="w-full p-4 h-[25vh] space-y-4 overflow-y-auto">
              {controls[activeIndex]}
            </div>

            {/* Control Switcher Buttons */}
            <div className="flex justify-center items-center space-x-4 fixed bottom-5 z-50 right-0 left-0">
              {icons.map((icon, index) => (
                <button
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  className={`flex items-center justify-center rounded-full transition duration-200 ${
                    activeIndex === index
                      ? "bg-neutral text-white"
                      : "bg-base-200 text-base-content/80"
                  } ${index === 3 ? "w-14 h-14" : "w-12 h-12"}`}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="w-full md:w-1/2 flex-col items-center justify-center hidden md:block">
          <div className="w-full p-4 h-[60vh] space-y-4 overflow-y-auto">
            {controls[activeIndex]}
          </div>

          <div className="flex justify-center items-center space-x-4 fixed bottom-5 z-50 right-0 left-0">
            {icons.map((icon, index) => (
              <button
                key={index}
                onClick={() => setActiveIndex(index)}
                className={`flex items-center justify-center rounded-full transition duration-200 ${
                  activeIndex === index
                    ? "bg-neutral text-white"
                    : "bg-base-200 text-base-content/80"
                } ${index === 3 ? "w-14 h-14" : "w-12 h-12"}`}
              >
                {icon}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstagramStoryEditor;
